import React, { useState } from "react";

interface StaffEntry {
  id: string;
  kind: "supervision" | "recognition" | "checkin";
  staffName: string;
  date: string;
  nextDue: string;
  goals: string;
  notes: string;
  reason: string;
  stress: number;
  support: string;
  action: string;
}

const KEY = "staff_dev_records";

function load(): StaffEntry[] {
  try {
    return JSON.parse(localStorage.getItem(KEY) || "[]");
  } catch {
    return [];
  }
}

const blank = (kind: StaffEntry["kind"]): StaffEntry => ({
  id: `sd-${Date.now()}`,
  kind,
  staffName: "",
  date: new Date().toISOString().slice(0, 10),
  nextDue: "",
  goals: "",
  notes: "",
  reason: "",
  stress: 3,
  support: "",
  action: "",
});

export default function StaffWellbeing({ user }: { user?: any }) {
  const [entries, setEntries] = useState<StaffEntry[]>(load);
  const [form, setForm] = useState<StaffEntry | null>(null);
  const [message, setMessage] = useState("");

  const save = (next: StaffEntry[]) => {
    setEntries(next);
    localStorage.setItem(KEY, JSON.stringify(next));
  };

  const set = (key: keyof StaffEntry, value: any) => setForm(f => (f ? { ...f, [key]: value } : f));

  const saveForm = () => {
    if (!form) return;
    if (!form.staffName && form.kind !== "checkin") {
      setMessage("Enter the staff member's name (or leave blank for anonymous check-ins).");
      return;
    }
    const exists = entries.some(e => e.id === form.id);
    save(exists ? entries.map(e => (e.id === form.id ? form : e)) : [form, ...entries]);
    setForm(null);
    setMessage("Saved.");
  };

  const remove = (id: string) => save(entries.filter(e => e.id !== id));

  const daysTo = (d: string) => Math.ceil((new Date(d).getTime() - Date.now()) / 86400000);

  const supervisions = entries.filter(e => e.kind === "supervision");
  const overdue = supervisions.filter(e => e.nextDue && daysTo(e.nextDue) < 0);
  const dueSoon = supervisions.filter(e => e.nextDue && daysTo(e.nextDue) >= 0 && daysTo(e.nextDue) <= 30);
  const recognitions = entries.filter(e => e.kind === "recognition");
  const checkins = entries.filter(e => e.kind === "checkin");
  const avgStress = checkins.length ? Math.round((checkins.reduce((s, c) => s + c.stress, 0) / checkins.length) * 10) / 10 : 0;

  return (
    <div className="space-y-6 p-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-900">Staff Development & Wellbeing</h2>
        <p className="text-sm text-slate-600">Developed, supported and heard staff are the heart of Outstanding. Supervisions, goals, wellbeing check-ins and recognition.</p>
      </div>

      <div className="grid gap-3 md:grid-cols-4">
        <div className={`rounded-xl border p-4 text-center ${overdue.length ? "border-red-300 bg-red-50" : "border-green-300 bg-green-50"}`}><div className={`text-2xl font-bold ${overdue.length ? "text-red-700" : "text-green-700"}`}>{overdue.length}</div><div className={`text-xs ${overdue.length ? "text-red-700" : "text-green-700"}`}>Supervisions overdue</div></div>
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-center"><div className="text-2xl font-bold text-amber-700">{dueSoon.length}</div><div className="text-xs text-amber-700">Due within 30 days</div></div>
        <div className="rounded-xl border border-green-300 bg-green-50 p-4 text-center"><div className="text-2xl font-bold text-green-700">{recognitions.length}</div><div className="text-xs text-green-700">Recognition moments</div></div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 text-center shadow-sm"><div className="text-2xl font-bold text-slate-900">{avgStress || "-"}</div><div className="text-xs text-slate-500">Avg stress (1-5, lower better)</div></div>
      </div>

      {message ? <div className="rounded-lg bg-blue-50 p-3 text-sm text-blue-900">{message}</div> : null}

      {form ? (
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <h3 className="mb-3 font-semibold text-slate-900">{form.kind === "supervision" ? "Supervision & development" : form.kind === "recognition" ? "Recognition" : "Wellbeing check-in"}</h3>
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">{form.kind === "checkin" ? "Staff name (optional - can be anonymous)" : "Staff member"}</label>
              <input value={form.staffName} onChange={e => set("staffName", e.target.value)} className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Date</label>
              <input type="date" value={form.date} onChange={e => set("date", e.target.value)} className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" />
            </div>
            {form.kind === "supervision" ? (
              <>
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-600">Next supervision due</label>
                  <input type="date" value={form.nextDue} onChange={e => set("nextDue", e.target.value)} className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" />
                </div>
                <div className="md:col-span-2">
                  <label className="mb-1 block text-xs font-medium text-slate-600">Development goals & career pathway</label>
                  <textarea value={form.goals} onChange={e => set("goals", e.target.value)} rows={2} className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" />
                </div>
                <div className="md:col-span-2">
                  <label className="mb-1 block text-xs font-medium text-slate-600">Quality conversation notes (including wellbeing)</label>
                  <textarea value={form.notes} onChange={e => set("notes", e.target.value)} rows={2} className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" />
                </div>
              </>
            ) : null}
            {form.kind === "recognition" ? (
              <div className="md:col-span-2">
                <label className="mb-1 block text-xs font-medium text-slate-600">What did they do?</label>
                <textarea value={form.reason} onChange={e => set("reason", e.target.value)} rows={2} className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" />
              </div>
            ) : null}
            {form.kind === "checkin" ? (
              <>
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-600">Stress level (1 low - 5 high)</label>
                  <select value={String(form.stress)} onChange={e => set("stress", parseInt(e.target.value, 10))} className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm">
                    <option value="1">1</option><option value="2">2</option><option value="3">3</option><option value="4">4</option><option value="5">5</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-600">Support needed</label>
                  <input value={form.support} onChange={e => set("support", e.target.value)} className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" />
                </div>
                <div className="md:col-span-2">
                  <label className="mb-1 block text-xs font-medium text-slate-600">Action taken (debrief, EAP referral, rota change...)</label>
                  <input value={form.action} onChange={e => set("action", e.target.value)} className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" />
                </div>
              </>
            ) : null}
          </div>
          <div className="mt-3 flex gap-2">
            <button onClick={saveForm} className="rounded-lg bg-green-700 px-4 py-2 text-sm font-medium text-white">Save</button>
            <button onClick={() => setForm(null)} className="rounded-lg border border-slate-300 px-4 py-2 text-sm">Cancel</button>
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <h3 className="mb-3 font-semibold text-slate-900">Add a record</h3>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setForm(blank("supervision"))} className="rounded-lg bg-blue-900 px-4 py-2 text-sm font-medium text-white">+ Supervision</button>
            <button onClick={() => setForm(blank("recognition"))} className="rounded-lg bg-green-700 px-4 py-2 text-sm font-medium text-white">+ Recognition</button>
            <button onClick={() => setForm(blank("checkin"))} className="rounded-lg bg-slate-700 px-4 py-2 text-sm font-medium text-white">+ Wellbeing check-in</button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {entries.map(e => (
          <div key={e.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-sm font-medium text-slate-900">
                {e.staffName || "Anonymous"} <span className="text-xs text-slate-500">{e.kind === "supervision" ? `Supervision ${e.date}${e.nextDue ? ` | next due ${e.nextDue}` : ""}` : e.kind === "recognition" ? `Recognition ${e.date}` : `Check-in ${e.date} | stress ${e.stress}/5`}</span>
              </div>
              <div className="flex items-center gap-2">
                {e.kind === "supervision" && e.nextDue ? <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${daysTo(e.nextDue) < 0 ? "bg-red-100 text-red-700" : daysTo(e.nextDue) <= 30 ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"}`}>{daysTo(e.nextDue) < 0 ? "Overdue" : `Due in ${daysTo(e.nextDue)}d`}</span> : null}
                <button onClick={() => setForm(e)} className="rounded-lg border border-slate-300 px-2 py-1 text-xs">Edit</button>
                <button onClick={() => remove(e.id)} className="text-xs text-red-600">Remove</button>
              </div>
            </div>
            <div className="mt-1 text-xs text-slate-600">
              {e.kind === "supervision" ? `${e.goals ? `Goals: ${e.goals} ` : ""}${e.notes ? `| ${e.notes}` : ""}` : e.kind === "recognition" ? e.reason : `${e.support ? `Support: ${e.support} ` : ""}${e.action ? `| Action: ${e.action}` : ""}`}
            </div>
          </div>
        ))}
        {entries.length === 0 ? <div className="rounded-lg bg-slate-50 p-4 text-sm text-slate-500">No staff development records yet. Start with supervisions - every staff member should have one every 6-8 weeks.</div> : null}
      </div>
    </div>
  );
}